import React from "react";
import moment from "moment";
import {
  makeStyles,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  FormControlLabel,
  InputAdornment,
  IconButton,
} from "@material-ui/core";
import ExpandMore from "@material-ui/icons/ExpandMore";
import ChevronRight from "@material-ui/icons/ChevronRight";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import AddIcon from "@material-ui/icons/Add";
import theme from "../../theme";
import CommonButton from "../button";
import CommonSwitch from "../switch";
import EmployerSelect from "../employerSelect";
import {
  rewardSummary,
  rewardWarning,
  formatRedemptions,
  budgetState,
  isRunningNow,
  runningTooltip,
  formatMoney,
  channelSummary,
  humanizeRule,
  summarizeEligibility,
} from "./displayUtils";
import { Pagination } from "@material-ui/lab";
import CommonTable from "../table";
import CommonInput from "../input";
import CommonDropDown from "../dropdown";
import {
  getCampaignConfigAction,
  getCampaignListAction,
  getCampaignDetailAction,
  createCampaignAction,
  updateCampaignAction,
  campaignWorkflowAction,
  getCampaignStatsAction,
  getCampaignHistoryAction,
  setCampaignFiltersAction,
  setCampaignPaginationAction,
  setCampaignHistoryPaginationAction,
  resetCampaignFormErrorsAction,
  clearCampaignToastAction,
} from "../../containers/dashboard/action";
import { useDispatch, useSelector } from "react-redux";
import { useSnackbar } from "notistack";
import {
  selectCampaignConfig,
  selectCampaignHistory,
  selectCampaignList,
  selectCampaignState,
  selectCampaignStats,
  selectCampaignUIFlags,
} from "../../containers/dashboard/selectors";
import {
  getRequiredFieldsFromConfig,
  validateCampaignForm,
  buildCampaignPayload,
  getRuleValueKind,
} from "../../containers/dashboard/campaignUtils";

const useStyles = makeStyles(() => ({
  root: {
    width: "100%",
    minHeight: "calc(100vh - 175px)",
    padding: "45px 90px",
    marginLeft: "200px",
    overflowX: "hidden",
  },
  header: { display: "flex", justifyContent: "space-between", alignItems: "center" },
  title: {
    margin: 0,
    fontFamily: theme.fontFamily.MARKPROBOLD,
    color: theme.colors.DodgerBlue,
    fontSize: "24px",
  },
  toolbar: {
    marginTop: "14px",
    marginBottom: "14px",
    display: "grid",
    gridTemplateColumns: "repeat(5, minmax(180px, 1fr))",
    gap: "10px",
  },
  sectionCard: {
    marginTop: "18px",
    padding: "16px",
    border: "1px solid #E6EFFC",
    borderRadius: "10px",
    backgroundColor: "#FFFFFF",
  },
  actionsRow: { display: "flex", gap: "8px", flexWrap: "wrap", marginBottom: "10px" },
  inlineError: { color: "red", fontSize: "12px", marginTop: "-10px", marginBottom: "10px" },
  grid2: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" },
  grid3: { display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "12px" },
  checkboxRow: { display: "flex", gap: "18px", marginTop: "8px", marginBottom: "12px" },
  ruleRow: {
    border: "1px solid #E6EFFC",
    borderRadius: "10px",
    padding: "12px",
    marginBottom: "10px",
    background: "#F9FBFF",
  },
  stateTag: { textTransform: "uppercase", fontWeight: 600 },
  rewardChip: {
    backgroundColor: "#ECF3FF",
    color: theme.colors.DodgerBlue,
    fontFamily: theme.fontFamily.MARKPROMEDIUM,
    maxWidth: "180px",
    "& .MuiChip-label": { whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
  },
  channelChip: {
    backgroundColor: theme.colors.Zumthor,
    color: theme.colors.Violet,
    fontFamily: theme.fontFamily.MARKPROMEDIUM,
    fontSize: "11px",
    height: "22px",
    maxWidth: "180px",
    "& .MuiChip-label": { whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", padding: "0 8px" },
  },
  channelChipWarning: {
    backgroundColor: theme.colors.Cinderella,
    color: theme.colors.RejectRed,
  },
  rewardCellStack: { display: "flex", flexDirection: "column", gap: "4px", alignItems: "flex-start" },
  budgetCell: { display: "flex", flexDirection: "column", gap: "4px", minWidth: "110px" },
  budgetBarTrack: {
    width: "100%",
    height: "6px",
    backgroundColor: "#ECF3FF",
    borderRadius: "3px",
    overflow: "hidden",
  },
  budgetBarFill: { height: "100%", borderRadius: "3px", transition: "width 0.2s" },
  budgetBarNormal: { backgroundColor: theme.colors.ApproveGreen },
  budgetBarWarning: { backgroundColor: "#B7791F" },
  budgetBarDanger: { backgroundColor: theme.colors.RejectRed },
  budgetLabel: {
    fontSize: "11px",
    color: theme.colors.Violet,
    fontFamily: theme.fontFamily.MARKPROBOOK,
    whiteSpace: "nowrap",
  },
  runningDot: {
    display: "inline-block",
    width: "10px",
    height: "10px",
    borderRadius: "50%",
  },
  runningDotOn: { backgroundColor: theme.colors.ApproveGreen, boxShadow: "0 0 0 3px rgba(22, 163, 74, 0.15)" },
  runningDotOff: { backgroundColor: "#CBD5E1" },

  // Form layout — sections + side preview
  formLayout: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) 320px",
    gap: "20px",
    marginTop: "18px",
    "@media (max-width: 1280px)": { gridTemplateColumns: "minmax(0, 1fr)" },
  },
  formColumn: { display: "flex", flexDirection: "column", gap: "16px", minWidth: 0 },
  formCard: {
    background: theme.colors.White,
    border: `1px solid ${theme.colors.Zumthor}`,
    borderRadius: "12px",
    padding: "20px 22px",
    boxShadow: "0px 1px 6px rgba(208, 207, 219, 0.25)",
  },
  sectionToggle: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    cursor: "pointer",
    userSelect: "none",
    padding: "4px 0",
    "&:hover .sectionCaret": { color: theme.colors.DodgerBlue },
  },
  sectionTitleWrap: { flex: 1, display: "flex", flexDirection: "column", gap: "2px", minWidth: 0 },
  sectionCaret: {
    color: theme.colors.DodgerBlueLight,
    fontSize: "22px",
    transition: "transform 0.15s ease",
  },
  sectionStatusComplete: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    fontSize: "11px",
    fontFamily: theme.fontFamily.MARKPROMEDIUM,
    color: theme.colors.ApproveGreen,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
  },
  sectionStatusPending: {
    display: "inline-flex",
    alignItems: "center",
    fontSize: "11px",
    fontFamily: theme.fontFamily.MARKPROMEDIUM,
    color: "#64748B",
    textTransform: "uppercase",
    letterSpacing: "0.04em",
  },
  sectionStatusMissing: {
    display: "inline-flex",
    alignItems: "center",
    fontSize: "11px",
    fontFamily: theme.fontFamily.MARKPROMEDIUM,
    color: theme.colors.RejectRed,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
  },
  sectionBody: { marginTop: "14px" },
  formSectionHeader: { display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: "14px" },
  formSectionTitle: {
    margin: 0,
    fontSize: "15px",
    fontFamily: theme.fontFamily.MARKPROBOLD,
    color: theme.colors.DodgerBlue,
  },
  formSectionHint: {
    fontSize: "12px",
    color: "#64748B",
    fontFamily: theme.fontFamily.MARKPRO,
  },
  formGrid2: { display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "14px" },
  formGrid3: { display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: "14px" },
  fieldShell: { display: "flex", flexDirection: "column", gap: "6px", minWidth: 0 },
  fieldLabel: {
    fontSize: "12px",
    fontFamily: theme.fontFamily.MARKPROMEDIUM,
    color: theme.colors.Violet,
  },
  fieldRequired: { color: theme.colors.RejectRed, marginLeft: "2px" },
  fieldError: {
    fontSize: "11px",
    color: theme.colors.RejectRed,
    fontFamily: theme.fontFamily.MARKPRO,
    marginTop: "2px",
  },
  fieldHint: {
    fontSize: "11px",
    color: "#64748B",
    fontFamily: theme.fontFamily.MARKPRO,
  },
  switchRow: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: "8px 18px",
    marginTop: "4px",
    "@media (max-width: 720px)": { gridTemplateColumns: "repeat(2, minmax(0, 1fr))" },
  },
  switchLabel: { fontSize: "13px", color: theme.colors.Violet, fontFamily: theme.fontFamily.MARKPROMEDIUM },
  ruleCard: {
    background: theme.colors.Porcelain,
    border: `1px solid ${theme.colors.Zumthor}`,
    borderRadius: "10px",
    padding: "14px 16px",
    marginBottom: "12px",
    display: "flex",
    flexDirection: "column",
    gap: "10px",
  },
  ruleHeader: { display: "flex", justifyContent: "space-between", alignItems: "center" },
  ruleTitle: { fontSize: "13px", fontFamily: theme.fontFamily.MARKPROBOLD, color: theme.colors.Violet },
  addRuleRow: { display: "flex", justifyContent: "flex-start", marginTop: "4px" },
  addRuleBtn: {
    color: theme.colors.DodgerBlue,
    fontFamily: theme.fontFamily.MARKPROMEDIUM,
    textTransform: "none",
    borderColor: theme.colors.DodgerBlue,
  },
  formFooter: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "12px",
    marginTop: "8px",
    padding: "16px 22px",
    background: theme.colors.White,
    border: `1px solid ${theme.colors.Zumthor}`,
    borderRadius: "12px",
    position: "sticky",
    bottom: "12px",
    boxShadow: "0px 4px 12px rgba(208, 207, 219, 0.35)",
  },
  previewWrap: {
    position: "sticky",
    top: "20px",
    alignSelf: "flex-start",
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
  previewCard: {
    background: theme.colors.White,
    border: `1px solid ${theme.colors.Zumthor}`,
    borderRadius: "12px",
    padding: "18px 20px",
    boxShadow: "0px 1px 6px rgba(208, 207, 219, 0.25)",
  },
  previewLabel: {
    fontSize: "11px",
    fontFamily: theme.fontFamily.MARKPROBOLD,
    color: theme.colors.DodgerBlue,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    marginBottom: "10px",
  },
  previewRewardChip: {
    backgroundColor: "#ECF3FF",
    color: theme.colors.DodgerBlue,
    fontFamily: theme.fontFamily.MARKPROBOLD,
    fontSize: "13px",
    height: "32px",
  },
  previewMobileFrame: {
    background: theme.colors.Porcelain,
    border: `1px solid ${theme.colors.Zumthor}`,
    borderRadius: "14px",
    padding: "14px",
    display: "flex",
    flexDirection: "column",
    gap: "10px",
  },
  previewNotification: {
    background: theme.colors.White,
    border: `1px solid ${theme.colors.Zumthor}`,
    borderRadius: "10px",
    padding: "10px 12px",
  },
  previewNotificationTitle: {
    fontSize: "13px",
    fontFamily: theme.fontFamily.MARKPROBOLD,
    color: theme.colors.Violet,
    marginBottom: "2px",
  },
  previewNotificationBody: {
    fontSize: "12px",
    fontFamily: theme.fontFamily.MARKPRO,
    color: theme.colors.Violet,
    lineHeight: 1.4,
  },
  previewBanner: {
    background: `linear-gradient(135deg, ${theme.colors.DodgerBlue}, ${theme.colors.Violet})`,
    color: theme.colors.White,
    borderRadius: "10px",
    padding: "14px 16px",
  },
  previewBannerTitle: { fontSize: "13px", fontFamily: theme.fontFamily.MARKPROBOLD, marginBottom: "4px" },
  previewBannerBody: { fontSize: "12px", fontFamily: theme.fontFamily.MARKPRO, opacity: 0.9, lineHeight: 1.4 },
  previewPlaceholder: { fontSize: "12px", color: "#94A3B8", fontFamily: theme.fontFamily.MARKPRO, fontStyle: "italic" },
  previewSmsWrap: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    alignItems: "flex-start",
  },
  previewSmsSender: {
    fontSize: "10px",
    fontFamily: theme.fontFamily.MARKPROBOLD,
    color: "#64748B",
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    paddingLeft: "8px",
  },
  previewSmsBubble: {
    background: theme.colors.ApproveGreen,
    color: theme.colors.White,
    borderRadius: "16px",
    borderBottomLeftRadius: "4px",
    padding: "8px 12px",
    fontSize: "12px",
    fontFamily: theme.fontFamily.MARKPRO,
    maxWidth: "85%",
    lineHeight: 1.35,
    boxShadow: "0px 1px 3px rgba(22, 163, 74, 0.25)",
    wordBreak: "break-word",
  },

  detailLayout: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) 320px",
    gap: "20px",
    marginTop: "18px",
    "@media (max-width: 1280px)": { gridTemplateColumns: "minmax(0, 1fr)" },
  },
  detailColumn: { display: "flex", flexDirection: "column", gap: "16px", minWidth: 0 },
  detailCard: {
    background: theme.colors.White,
    border: `1px solid ${theme.colors.Zumthor}`,
    borderRadius: "12px",
    padding: "18px 20px",
    boxShadow: "0px 1px 6px rgba(208, 207, 219, 0.25)",
  },
  detailLabel: {
    fontSize: "11px",
    fontFamily: theme.fontFamily.MARKPROBOLD,
    color: theme.colors.DodgerBlue,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    marginBottom: "10px",
  },
  detailTitle: {
    margin: 0,
    fontSize: "20px",
    fontFamily: theme.fontFamily.MARKPROBOLD,
    color: theme.colors.DodgerBlue,
  },
  detailCode: { fontSize: "13px", color: "#64748B", fontFamily: theme.fontFamily.MARKPRO, marginLeft: "8px" },
  detailHeaderRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: "12px",
    flexWrap: "wrap",
    marginBottom: "16px",
  },
  detailHeaderChips: { display: "flex", gap: "8px", flexWrap: "wrap", alignItems: "center", marginTop: "6px" },
  detailMetaGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
    gap: "14px 22px",
  },
  detailMetaLabel: { fontSize: "11px", color: "#64748B", fontFamily: theme.fontFamily.MARKPRO, textTransform: "uppercase", letterSpacing: "0.04em" },
  detailMetaValue: { fontSize: "14px", color: theme.colors.Violet, fontFamily: theme.fontFamily.MARKPROMEDIUM, marginTop: "2px" },
  ruleItem: {
    padding: "10px 12px",
    background: theme.colors.Porcelain,
    border: `1px solid ${theme.colors.Zumthor}`,
    borderRadius: "8px",
    fontSize: "13px",
    fontFamily: theme.fontFamily.MARKPRO,
    color: theme.colors.Violet,
    marginBottom: "8px",
  },
  ruleMeta: { fontSize: "11px", color: "#64748B", marginTop: "4px" },
  emptyHint: { fontSize: "13px", color: "#64748B", fontFamily: theme.fontFamily.MARKPRO, fontStyle: "italic" },
  warningChip: {
    backgroundColor: theme.colors.Cinderella,
    color: theme.colors.RejectRed,
    fontFamily: theme.fontFamily.MARKPROMEDIUM,
    fontSize: "12px",
  },
  auditRow: {
    display: "grid",
    gridTemplateColumns: "minmax(120px, 160px) minmax(0, 1fr) minmax(140px, auto)",
    gap: "12px",
    padding: "10px 0",
    borderBottom: `1px solid ${theme.colors.Zumthor}`,
    alignItems: "center",
    fontSize: "13px",
    fontFamily: theme.fontFamily.MARKPRO,
    color: theme.colors.Violet,
    "&:last-child": { borderBottom: "none" },
  },
  auditAction: { fontFamily: theme.fontFamily.MARKPROMEDIUM, color: theme.colors.DodgerBlue, textTransform: "uppercase", fontSize: "12px" },
  auditActor: { color: "#64748B" },
  auditTime: { fontSize: "12px", color: "#64748B", whiteSpace: "nowrap" },
  detailLoading: {
    minHeight: "260px",
    marginTop: "18px",
    background: theme.colors.White,
    border: `1px solid ${theme.colors.Zumthor}`,
    borderRadius: "12px",
    boxShadow: "0px 1px 6px rgba(208, 207, 219, 0.25)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "14px",
    padding: "32px",
  },
  detailLoadingLabel: {
    fontSize: "14px",
    fontFamily: theme.fontFamily.MARKPROMEDIUM,
    color: theme.colors.DodgerBlue,
  },
  detailLoadingHint: {
    fontSize: "12px",
    fontFamily: theme.fontFamily.MARKPRO,
    color: "#64748B",
  },
  paginationWrap: { marginTop: "14px", display: "flex", justifyContent: "center" },
  errorContainer: { background: "#fff0f0", color: "#c62828", padding: "10px", borderRadius: "8px", marginBottom: "10px" },
  successContainer: { background: "#effcf3", color: "#1b5e20", padding: "10px", borderRadius: "8px", marginBottom: "10px" },
  infoContainer: { background: "#eff6ff", color: "#1e3a8a", padding: "10px", borderRadius: "8px", marginBottom: "10px" },
}));

const emptyRule = {
  rule_type: "",
  operator: "",
  logic: "",
  group: 1,
  value_number: "",
  value_text: "",
  value_json: "",
};

const emptyForm = {
  name: "",
  code: "",
  description: "",
  campaign_type: "",
  state: "",
  start_at: "",
  end_at: "",
  is_recurring: false,
  recurrence_type: "",
  priority: 1,
  is_enabled: true,
  country: "",
  reward_kind: "",
  flat_amount: "",
  percent_value: "",
  fee_waiver: false,
  reward_currency: "",
  max_redemptions_per_user: "",
  cap_window: "",
  max_total_redemptions: "",
  max_budget: "",
  banner_title: "",
  banner_body: "",
  notification_title: "",
  notification_body: "",
  send_email: false,
  send_in_app: true,
  send_sms: false,
  eligibility_rules: [{ ...emptyRule }],
};

const toOption = (items = []) => items.map((item) => ({ value: item.value, label: item.label }));
const boolOptions = [
  { value: "", label: "Any" },
  { value: "true", label: "Enabled" },
  { value: "false", label: "Disabled" },
];

// Labeled dropdown shell — CommonDropDown is a thin react-select wrapper with
// no error/required props, so the visual surface (label, asterisk, inline
// error) lives here. Must live at module scope: defining it inside the page
// component gives it a new identity on every render, which unmounts everything
// inside (including EmployerSelect), re-firing the company-fetch effect in a
// loop. Receives `classes` so the styling still tracks the page's makeStyles.
const FieldDropdown = ({ classes, label, required, error, hint, children }) => (
  <div className={classes.fieldShell}>
    <span className={classes.fieldLabel}>
      {label}
      {required ? <span className={classes.fieldRequired}>*</span> : null}
    </span>
    {children}
    {error ? <span className={classes.fieldError}>{error}</span> : null}
    {!error && hint ? <span className={classes.fieldHint}>{hint}</span> : null}
  </div>
);

export default function CampaignEnginePage() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const campaignState = useSelector(selectCampaignState);
  const config = useSelector(selectCampaignConfig);
  const list = useSelector(selectCampaignList);
  const detail = campaignState?.detail;
  const stats = useSelector(selectCampaignStats);
  const history = useSelector(selectCampaignHistory);
  const ui = useSelector(selectCampaignUIFlags);
  // Only superadmins may approve / activate / submit review / end a campaign.
  const currentUser = useSelector((s) => s.loginReducer?.me);
  const isSuper = React.useMemo(() => {
    if (currentUser) return !!currentUser.is_super;
    try {
      return !!JSON.parse(localStorage.getItem("user"))?.is_super;
    } catch (e) {
      return false;
    }
  }, [currentUser]);
  const [mode, setMode] = React.useState("list");
  const [form, setForm] = React.useState(emptyForm);
  const [fieldErrors, setFieldErrors] = React.useState({});
  const [ruleErrors, setRuleErrors] = React.useState({});
  const [selectedCampaignId, setSelectedCampaignId] = React.useState(null);
  const [actionModal, setActionModal] = React.useState({ open: false, type: "", comment: "" });

  // Section open state — Basics opens by default so the user lands on a
  // meaningful first card. Other sections collapse to keep the page short and
  // let the admin focus on one block at a time. Clicking any header toggles.
  const [openSections, setOpenSections] = React.useState({
    basics: true,
    schedule: false,
    reward: false,
    caps: false,
    messaging: false,
    eligibility: false,
  });
  const toggleSection = (key) =>
    setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));

  const filters = campaignState?.filters || {};
  const pagination = campaignState?.pagination || { page: 1, page_size: 10 };
  const historyPagination = campaignState?.historyPagination || { page: 1, page_size: 10 };

  React.useEffect(() => {
    dispatch(getCampaignConfigAction());
  }, [dispatch]);

  React.useEffect(() => {
    dispatch(getCampaignListAction({ ...filters, ...pagination }));
  }, [dispatch, filters, pagination]);

  React.useEffect(() => {
    if (selectedCampaignId && mode === "detail") {
      dispatch(getCampaignDetailAction(selectedCampaignId));
      dispatch(getCampaignStatsAction(selectedCampaignId));
      dispatch(getCampaignHistoryAction(selectedCampaignId, historyPagination));
    }
  }, [dispatch, selectedCampaignId, mode, historyPagination]);

  // Bridge the reducer's `toast` slot into notistack so the campaign engine uses
  // the same notification system as the rest of the dashboard (bonuses, engagement).
  // For errors, prefer the detailed ui.error message (which carries the backend's
  // field-level reason) over the reducer's generic "Failed to create campaign".
  React.useEffect(() => {
    if (!ui?.toast?.message) return;
    const isError = ui.toast.type === "error";
    let message = ui.toast.message;
    if (isError && ui?.error) {
      if (Array.isArray(ui.error)) {
        message = ui.error.filter(Boolean).join(" · ") || message;
      } else if (typeof ui.error === "string") {
        message = ui.error;
      }
    }
    enqueueSnackbar(message, { variant: isError ? "error" : "success" });
    dispatch(clearCampaignToastAction());
  }, [ui?.toast, ui?.error, enqueueSnackbar, dispatch]);

  const onFilterChange = (key, value) => {
    dispatch(setCampaignFiltersAction({ [key]: value }));
    dispatch(setCampaignPaginationAction({ page: 1 }));
  };

  const onChangeForm = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const toFormFromDetail = React.useCallback((campaign) => {
    const rules = (campaign?.eligibility_rules || []).length ? campaign.eligibility_rules : [{ ...emptyRule }];
    return {
      ...emptyForm,
      ...campaign,
      priority: campaign?.priority ?? "",
      flat_amount: campaign?.flat_amount ?? "",
      percent_value: campaign?.percent_value ?? "",
      max_redemptions_per_user: campaign?.max_redemptions_per_user ?? "",
      max_total_redemptions: campaign?.max_total_redemptions ?? "",
      max_budget: campaign?.max_budget ?? "",
      eligibility_rules: rules.map((rule) => {
        // The wire stores list values as arrays under value_json; the form's
        // adaptive input wants a comma-string. Reverse the routing here so an
        // EMPLOYER rule loads as "acme, globex" rather than '["acme","globex"]'.
        const kind = getRuleValueKind(rule?.rule_type);
        let displayValueJson = "";
        if (rule?.value_json !== null && rule?.value_json !== undefined) {
          if (kind === "list" && Array.isArray(rule.value_json)) {
            displayValueJson = rule.value_json.join(", ");
          } else {
            displayValueJson = JSON.stringify(rule.value_json);
          }
        }
        return {
          ...emptyRule,
          ...rule,
          group: rule?.group ?? 1,
          value_number: rule?.value_number ?? "",
          value_text: rule?.value_text ?? "",
          value_json: displayValueJson,
        };
      }),
    };
  }, []);

  const validate = React.useCallback(() => {
    const errors = validateCampaignForm(config, form);
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  }, [config, form]);

  const submitForm = () => {
    dispatch(resetCampaignFormErrorsAction());
    setRuleErrors({});
    if (!validate()) {
      enqueueSnackbar("Please fill in all required fields before saving.", { variant: "error" });
      return;
    }
    const { payload, ruleErrors: eachRuleErrors, hasInvalidRows } = buildCampaignPayload(form);
    if (hasInvalidRows) {
      setRuleErrors(eachRuleErrors);
      setFieldErrors((prev) => ({
        ...prev,
        eligibility_rules: "Please complete or remove invalid eligibility rule rows.",
      }));
      enqueueSnackbar("Some eligibility rules are incomplete — please review.", { variant: "error" });
      return;
    }
    const onSuccess = (res) => {
      const campaignId = res?.id || res?.data?.id || selectedCampaignId;
      if (campaignId) {
        setSelectedCampaignId(campaignId);
        setMode("detail");
      } else {
        setMode("list");
      }
    };
    if (mode === "create") {
      dispatch(createCampaignAction(payload, { onSuccess }));
    } else if (mode === "edit" && selectedCampaignId) {
      dispatch(updateCampaignAction(selectedCampaignId, payload, { onSuccess }));
    }
  };

  const openDetail = (campaign) => {
    setSelectedCampaignId(campaign.id);
    setMode("detail");
    dispatch(getCampaignDetailAction(campaign.id));
  };

  const launchCreate = () => {
    setFieldErrors({});
    setRuleErrors({});
    dispatch(resetCampaignFormErrorsAction());
    setForm(emptyForm);
    setMode("create");
  };

  const launchEdit = () => {
    setFieldErrors({});
    setRuleErrors({});
    dispatch(resetCampaignFormErrorsAction());
    setForm(toFormFromDetail(detail));
    setMode("edit");
  };

  const submitAction = () => {
    if (!selectedCampaignId) return;
    dispatch(
      campaignWorkflowAction(
        selectedCampaignId,
        actionModal.type,
        actionModal.comment ? { comment: actionModal.comment } : {},
        {
          onSuccess: () => {
            setActionModal({ open: false, type: "", comment: "" });
          },
        }
      )
    );
  };

  // Only DISCOUNT and CASHBACK are wired through to backend runtime logic right
  // now — FEE_WAIVER / GIVEAWAY / REFERRAL_BONUS are display-only or unhandled,
  // so we hide them in the creator until they're real. Re-enable by removing
  // the filter (or expanding ENABLED_CAMPAIGN_TYPES) once the backend is ready.
  const ENABLED_CAMPAIGN_TYPES = ["DISCOUNT", "CASHBACK"];

  const allCampaignTypes = toOption(config?.campaign_types || []);
  const allRewardKinds = toOption(config?.reward_kinds || []);

  // CASHBACK can't be a fee waiver — the runtime applies the cashback as a
  // post-tx credit, there's no fee to waive. Drop FEE_WAIVER from the
  // reward_kind picker when CASHBACK is the campaign type.
  const filteredRewardKinds =
    form.campaign_type === "CASHBACK"
      ? allRewardKinds.filter((k) => k.value !== "FEE_WAIVER")
      : allRewardKinds;

  const configOptions = {
    campaignTypes: allCampaignTypes.filter((t) => ENABLED_CAMPAIGN_TYPES.includes(t.value)),
    campaignStates: toOption(config?.campaign_states || []),
    rewardKinds: filteredRewardKinds,
    capWindows: toOption(config?.cap_windows || []),
    recurrenceTypes: toOption(config?.recurrence_types || []),
    ruleTypes: toOption(config?.rule_types || []),
    ruleOperators: toOption(config?.rule_operators || []),
    ruleLogics: toOption(config?.rule_logics || []),
  };
  const configReady = !!config;
  const requiredFields = React.useMemo(
    () => getRequiredFieldsFromConfig(config, form),
    [config, form]
  );
  const showFlatAmount = requiredFields?.reward?.includes("flat_amount");
  const showPercentValue = requiredFields?.reward?.includes("percent_value");
  // fee_waiver is derived from reward_kind, never user-toggled, so no toggle is
  // rendered. We keep the variable only to satisfy the reactive Save guard
  // below.
  const showCapWindow = requiredFields?.cap?.includes("cap_window") || !!form.max_redemptions_per_user;

  // Reactive guard for the Save button — mirrors the validate() checks so the
  // button visually communicates incompleteness instead of letting users press it
  // and get a toast. Toast still fires from submitForm if a stale state slips
  // through (e.g. user pressed Enter on a focused input).
  const hasValue = (v) => v !== undefined && v !== null && String(v).trim() !== "";
  const requiredFilled = (() => {
    if (!hasValue(form.name)) return false;
    if (!hasValue(form.code)) return false;
    if (!hasValue(form.campaign_type)) return false;
    if (!hasValue(form.reward_kind)) return false;
    if (!hasValue(form.start_at)) return false;
    if (!hasValue(form.end_at)) return false;
    if (!hasValue(form.priority)) return false;
    if (showFlatAmount && !hasValue(form.flat_amount)) return false;
    if (showPercentValue && !hasValue(form.percent_value)) return false;
    if (hasValue(form.max_redemptions_per_user) && !hasValue(form.cap_window)) return false;
    return true;
  })();

  // Switching campaign_type can leave stale reward fields from the previous type
  // (a CASHBACK draft must never ship with a leftover FEE_WAIVER flat_amount).
  // This single setter handles the reset so the form invariant holds.
  // fee_waiver is a derived legacy boolean — it must always mirror
  // (reward_kind === "FEE_WAIVER"). Setting it here keeps the wire payload
  // consistent without exposing a toggle that admins could desync.
  const onChangeCampaignType = (nextType) => {
    setForm((prev) => ({
      ...prev,
      campaign_type: nextType,
      // FEE_WAIVER campaigns auto-pick FEE_WAIVER reward_kind so admins don't
      // have to make the same selection twice.
      reward_kind: nextType === "FEE_WAIVER" ? "FEE_WAIVER" : "",
      flat_amount: "",
      percent_value: "",
      fee_waiver: nextType === "FEE_WAIVER",
    }));
  };

  const onChangeRewardKind = (nextKind) => {
    setForm((prev) => ({
      ...prev,
      reward_kind: nextKind,
      flat_amount: "",
      percent_value: "",
      fee_waiver: nextKind === "FEE_WAIVER",
    }));
  };

  const cancelForm = () => {
    setFieldErrors({});
    setRuleErrors({});
    dispatch(resetCampaignFormErrorsAction());
    if (mode === "edit" && selectedCampaignId) {
      setMode("detail");
    } else {
      setForm(emptyForm);
      setMode("list");
    }
  };

  // FieldDropdown lives at module scope (see top of file) — defining it inside
  // CampaignEnginePage gave it a fresh identity on every render, which
  // unmounted EmployerSelect on each keystroke and re-fired its
  // useEffect→dispatch→state→re-render loop. Pass `classes` through as a prop
  // since it still needs the page's makeStyles output.

  // Collapsible section card. The header acts as the toggle and surfaces a
  // status hint so a collapsed-but-complete section reads as filled.
  // status: "complete" | "pending" | "missing" — drives both the badge text
  // (passed in via statusLabel) and its color.


  // Per-section completion derivation — drives the small status pill in the
  // collapsed header so users can tell at a glance which sections are still
  // missing required input. Kept local so the SectionCard isn't responsible
  // for knowing the form shape.
  const sectionStatus = {
    basics:
      hasValue(form.name) && hasValue(form.code) && hasValue(form.campaign_type) && hasValue(form.priority)
        ? { status: "complete", label: "Complete" }
        : { status: "missing", label: "Required" },
    schedule:
      hasValue(form.start_at) && hasValue(form.end_at)
        ? { status: "complete", label: "Complete" }
        : { status: "missing", label: "Required" },
    reward:
      hasValue(form.reward_kind) &&
        (form.reward_kind === "FEE_WAIVER" ||
          (showFlatAmount && hasValue(form.flat_amount)) ||
          (showPercentValue && hasValue(form.percent_value)))
        ? { status: "complete", label: "Complete" }
        : { status: "missing", label: "Required" },
    caps:
      hasValue(form.max_redemptions_per_user) && !hasValue(form.cap_window)
        ? { status: "missing", label: "Pick window" }
        : (hasValue(form.max_redemptions_per_user) ||
          hasValue(form.max_total_redemptions) ||
          hasValue(form.max_budget))
          ? { status: "complete", label: "Set" }
          : { status: "pending", label: "Optional" },
    messaging:
      form.send_in_app || form.send_sms || form.send_email
        ? { status: "complete", label: `${[form.send_in_app && "Push", form.send_sms && "SMS", form.send_email && "Email"].filter(Boolean).join(" · ")}` }
        : { status: "missing", label: "Pick channel" },
    eligibility:
      (form.eligibility_rules || []).filter((r) => r?.rule_type).length === 0
        ? { status: "pending", label: "All users" }
        : { status: "complete", label: `${(form.eligibility_rules || []).filter((r) => r?.rule_type).length} rule${(form.eligibility_rules || []).filter((r) => r?.rule_type).length === 1 ? "" : "s"}` },
  };

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <h2 className={classes.title}>Campaign Engine</h2>
        <div style={{ display: "flex", gap: "8px" }}>
          {mode !== "list" && (
            <CommonButton
              type="button"
              text="Back to List"
              stylevariant="secondary2"
              onClick={() => setMode("list")}
              style={{ padding: "8px 20px" }}
            />
          )}
          <CommonButton
            type="button"
            text="Create Campaign"
            stylevariant="primary"
            onClick={launchCreate}
            style={{ padding: "8px 24px" }}
          />
        </div>
      </div>

      {!configReady && ui?.configLoading ? (
        <div className={classes.infoContainer}>Loading campaign configuration...</div>
      ) : null}
      {!configReady && ui?.error ? (
        <div className={classes.errorContainer} style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <span>Failed to load campaign configuration.</span>
          <CommonButton
            type="button"
            text="Retry"
            stylevariant="primary"
            onClick={() => dispatch(getCampaignConfigAction())}
            style={{ padding: "4px 16px" }}
          />
        </div>
      ) : null}


      {(mode === "list") && (
        <>
          <div className={classes.toolbar}>
            <CommonDropDown
              placeholder="State"
              options={[{ value: "", label: "All" }, ...configOptions.campaignStates]}
              value={[{ value: "", label: "All" }, ...configOptions.campaignStates].find((x) => x.value === filters.state) || null}
              onChange={(option) => onFilterChange("state", option?.value || "")}
              isClearable
              isDisabled={!configReady}
            />
            <CommonDropDown
              placeholder="Campaign Type"
              options={[{ value: "", label: "All" }, ...configOptions.campaignTypes]}
              value={[{ value: "", label: "All" }, ...configOptions.campaignTypes].find((x) => x.value === filters.campaign_type) || null}
              onChange={(option) => onFilterChange("campaign_type", option?.value || "")}
              isClearable
              isDisabled={!configReady}
            />
            <CommonDropDown
              placeholder="Enabled"
              options={boolOptions}
              value={boolOptions.find((x) => x.value === filters.is_enabled) || boolOptions[0]}
              onChange={(option) => onFilterChange("is_enabled", option?.value || "")}
              isClearable
            />
            <CommonInput
              label="Country"
              value={filters.country || ""}
              onChange={(e) => onFilterChange("country", e.target.value)}
              placeholder="Country code"
            />
            <CommonInput
              label="Search"
              value={filters.search || ""}
              onChange={(e) => onFilterChange("search", e.target.value)}
              placeholder="Search by name/code"
            />
          </div>
          <CommonTable
            loading={ui?.listLoading}
            data={list?.data || []}
            columns={[
              { Header: "Name", accessor: "name" },
              { Header: "Code", accessor: "code" },
              {
                Header: "Type",
                accessor: "campaign_type",
                Cell: (value) => <Chip size="small" label={value || "N/A"} />,
              },
              {
                Header: "State",
                accessor: "state",
                Cell: (value) => <Chip size="small" className={classes.stateTag} label={value || "N/A"} />,
              },
              { Header: "Country", accessor: "country" },
              {
                Header: "Reward & channel",
                accessor: "reward_kind",
                Cell: (_value, row) => {
                  const text = rewardSummary(row);
                  const ch = channelSummary(row);
                  return (
                    <div className={classes.rewardCellStack}>
                      <Tooltip title={text}>
                        <Chip size="small" className={classes.rewardChip} label={text} />
                      </Tooltip>
                      <Tooltip title={ch.warning || ch.text}>
                        <Chip
                          size="small"
                          className={`${classes.channelChip} ${ch.isEmpty ? classes.channelChipWarning : ""}`}
                          label={ch.text}
                        />
                      </Tooltip>
                    </div>
                  );
                },
              },
              {
                Header: "Redemptions",
                accessor: "redeemed_count",
                Cell: (_value, row) => formatRedemptions(row),
              },
              {
                Header: "Budget",
                accessor: "max_budget",
                Cell: (_value, row) => {
                  const bs = budgetState(row);
                  if (!bs.hasBudget) {
                    return <span className={classes.budgetLabel}>—</span>;
                  }
                  const fillClass =
                    bs.level === "danger"
                      ? classes.budgetBarDanger
                      : bs.level === "warning"
                        ? classes.budgetBarWarning
                        : classes.budgetBarNormal;
                  return (
                    <div className={classes.budgetCell}>
                      <div className={classes.budgetBarTrack}>
                        <div
                          className={`${classes.budgetBarFill} ${fillClass}`}
                          style={{ width: `${bs.pct}%` }}
                        />
                      </div>
                      <span className={classes.budgetLabel}>{Math.round(bs.pct)}% used</span>
                    </div>
                  );
                },
              },
              {
                Header: "Running",
                accessor: "is_enabled",
                Cell: (_value, row) => {
                  const on = isRunningNow(row);
                  return (
                    <Tooltip title={runningTooltip(row)}>
                      <span
                        className={`${classes.runningDot} ${on ? classes.runningDotOn : classes.runningDotOff}`}
                        aria-label={on ? "Running" : "Not running"}
                      />
                    </Tooltip>
                  );
                },
              },
            ]}
            onRowClick={(index) => openDetail((list?.data || [])[index])}
          />
          <div className={classes.paginationWrap}>
            <Pagination
              page={pagination.page}
              count={Math.max(1, Math.ceil((list?.count || 0) / (pagination.page_size || 10)))}
              onChange={(_, page) => dispatch(setCampaignPaginationAction({ page }))}
            />
          </div>
        </>
      )}

      {(mode === "create" || mode === "edit") && (
        <div className={classes.formLayout}>
          <div className={classes.formColumn}>
            {ui?.error && typeof ui.error === "string" ? (
              <div className={classes.errorContainer}>{ui.error}</div>
            ) : null}

            {/* Basics */}
            <SectionCard
              id="basics"
              title={`${mode === "create" ? "Create Campaign" : "Edit Campaign"} · Basics`}
              hint="Name, code, and type"
              status={sectionStatus.basics.status}
              statusLabel={sectionStatus.basics.label}
              openSections={openSections}
              toggleSection={toggleSection}
            >
              <div className={classes.formGrid2}>
                <CommonInput
                  label="Name"
                  value={form.name}
                  error={!!fieldErrors.name}
                  helperText={fieldErrors.name}
                  onChange={(e) => onChangeForm("name", e.target.value)}
                />
                <CommonInput
                  label="Code"
                  value={form.code}
                  error={!!fieldErrors.code}
                  helperText={fieldErrors.code}
                  onChange={(e) => onChangeForm("code", e.target.value)}
                />
              </div>
              <div style={{ marginTop: "14px" }}>
                <FieldDropdown classes={classes}
                  label="Campaign Type"
                  required
                  error={fieldErrors.campaign_type}
                  hint="Determines which reward kinds are available below"
                >
                  <CommonDropDown
                    placeholder="Choose a campaign type"
                    options={configOptions.campaignTypes}
                    value={configOptions.campaignTypes.find((x) => x.value === form.campaign_type) || null}
                    onChange={(o) => onChangeCampaignType(o?.value || "")}
                    isDisabled={!configReady}
                  />
                </FieldDropdown>
              </div>
              <div style={{ marginTop: "14px" }}>
                <CommonInput
                  label="Description"
                  multiline
                  minRows={3}
                  value={form.description}
                  onChange={(e) => onChangeForm("description", e.target.value)}
                />
              </div>
              <div className={classes.formGrid2} style={{ marginTop: "14px" }}>
                <CommonInput
                  label="Country"
                  placeholder="e.g. NG"
                  value={form.country || ""}
                  onChange={(e) => onChangeForm("country", e.target.value)}
                />
                <CommonInput
                  label="Priority *"
                  type="number"
                  value={form.priority}
                  error={!!fieldErrors.priority}
                  helperText={fieldErrors.priority || "Higher number wins when two eligible campaigns compete"}
                  onChange={(e) => onChangeForm("priority", e.target.value)}
                />
              </div>
            </SectionCard>

            {/* Schedule */}
            <SectionCard
              id="schedule"
              title="Schedule"
              hint="Times shown in your local timezone"
              status={sectionStatus.schedule.status}
              statusLabel={sectionStatus.schedule.label}
              openSections={openSections}
              toggleSection={toggleSection}
            >
              <div className={classes.formGrid2}>
                <CommonInput
                  label="Start At"
                  type="datetime-local"
                  value={form.start_at || ""}
                  error={!!fieldErrors.start_at}
                  helperText={fieldErrors.start_at}
                  onChange={(e) => onChangeForm("start_at", e.target.value)}
                />
                <CommonInput
                  label="End At"
                  type="datetime-local"
                  value={form.end_at || ""}
                  error={!!fieldErrors.end_at}
                  helperText={fieldErrors.end_at}
                  onChange={(e) => onChangeForm("end_at", e.target.value)}
                />
              </div>
              <div style={{ marginTop: "12px" }}>
                <FormControlLabel
                  control={
                    <CommonSwitch
                      checked={!!form.is_enabled}
                      onChange={(e) => onChangeForm("is_enabled", e.target.checked)}
                    />
                  }
                  classes={{ label: classes.switchLabel }}
                  label="Campaign is enabled"
                />
                <FormControlLabel
                  control={
                    <CommonSwitch
                      checked={!!form.is_recurring}
                      onChange={(e) => onChangeForm("is_recurring", e.target.checked)}
                    />
                  }
                  classes={{ label: classes.switchLabel }}
                  label="Recurring"
                />
              </div>
              {form.is_recurring ? (
                <div style={{ marginTop: "10px", maxWidth: "260px" }}>
                  <FieldDropdown classes={classes} label="Recurrence Type">
                    <CommonDropDown
                      placeholder="Pick a cadence"
                      options={configOptions.recurrenceTypes}
                      value={configOptions.recurrenceTypes.find((x) => x.value === form.recurrence_type) || null}
                      onChange={(o) => onChangeForm("recurrence_type", o?.value || "")}
                      isDisabled={!configReady}
                    />
                  </FieldDropdown>
                </div>
              ) : null}
            </SectionCard>

            {/* Reward */}
            <SectionCard
              id="reward"
              title="Reward"
              hint={form.campaign_type ? `Configured for ${form.campaign_type}` : "Pick a campaign type first"}
              status={sectionStatus.reward.status}
              statusLabel={sectionStatus.reward.label}
              openSections={openSections}
              toggleSection={toggleSection}
            >
              <div className={classes.formGrid2}>
                <FieldDropdown classes={classes}
                  label="Reward Kind"
                  required
                  error={fieldErrors.reward_kind}
                  hint={!form.campaign_type ? "Locked until campaign type is set" : undefined}
                >
                  <CommonDropDown
                    placeholder="Choose a reward kind"
                    options={configOptions.rewardKinds}
                    value={configOptions.rewardKinds.find((x) => x.value === form.reward_kind) || null}
                    onChange={(o) => onChangeRewardKind(o?.value || "")}
                    isDisabled={!configReady || !form.campaign_type}
                  />
                </FieldDropdown>
                <CommonInput
                  label="Reward Currency"
                  placeholder="NGN"
                  value={form.reward_currency || ""}
                  onChange={(e) => onChangeForm("reward_currency", e.target.value)}
                  disabled={!configReady}
                />
              </div>
              {showFlatAmount ? (
                <div style={{ marginTop: "14px", maxWidth: "300px" }}>
                  <CommonInput
                    label="Flat Amount *"
                    type="number"
                    value={form.flat_amount}
                    error={!!fieldErrors.flat_amount}
                    helperText={fieldErrors.flat_amount}
                    onChange={(e) => onChangeForm("flat_amount", e.target.value)}
                    disabled={!configReady}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          {form.reward_currency || "₦"}
                        </InputAdornment>
                      ),
                    }}
                  />
                </div>
              ) : null}
              {showPercentValue ? (
                <div style={{ marginTop: "14px", maxWidth: "300px" }}>
                  <CommonInput
                    label="Percent Value *"
                    type="number"
                    value={form.percent_value}
                    error={!!fieldErrors.percent_value}
                    helperText={fieldErrors.percent_value}
                    onChange={(e) => onChangeForm("percent_value", e.target.value)}
                    disabled={!configReady}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">%</InputAdornment>,
                    }}
                  />
                </div>
              ) : null}
              {/* fee_waiver is derived from reward_kind; no toggle rendered. */}
              {form.reward_kind === "FEE_WAIVER" ? (
                <div style={{ marginTop: "10px" }}>
                  <span className={classes.fieldHint}>
                    Reward is a full fee waiver — no amount or percent needed.
                  </span>
                </div>
              ) : null}
            </SectionCard>

            {/* Caps & Budget */}
            <SectionCard
              id="caps"
              title="Caps & Budget"
              hint="Independent of date range"
              status={sectionStatus.caps.status}
              statusLabel={sectionStatus.caps.label}
              openSections={openSections}
              toggleSection={toggleSection}
            >
              <div className={classes.formGrid2}>
                <CommonInput
                  label="Max Redemptions Per User"
                  type="number"
                  value={form.max_redemptions_per_user}
                  onChange={(e) => onChangeForm("max_redemptions_per_user", e.target.value)}
                />
                {showCapWindow ? (
                  <FieldDropdown classes={classes}
                    label="Cap Window"
                    required
                    error={fieldErrors.cap_window}
                    hint="Window the per-user cap resets over"
                  >
                    <CommonDropDown
                      placeholder="DAY / WEEK / MONTH / LIFETIME"
                      options={configOptions.capWindows}
                      value={configOptions.capWindows.find((x) => x.value === form.cap_window) || null}
                      onChange={(o) => onChangeForm("cap_window", o?.value || "")}
                      isDisabled={!configReady}
                    />
                  </FieldDropdown>
                ) : null}
              </div>
              <div className={classes.formGrid2} style={{ marginTop: "14px" }}>
                <CommonInput
                  label="Max Total Redemptions"
                  type="number"
                  value={form.max_total_redemptions}
                  onChange={(e) => onChangeForm("max_total_redemptions", e.target.value)}
                />
                <CommonInput
                  label="Max Budget"
                  type="number"
                  value={form.max_budget}
                  onChange={(e) => onChangeForm("max_budget", e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        {form.reward_currency || "₦"}
                      </InputAdornment>
                    ),
                  }}
                />
              </div>
            </SectionCard>

            {/* Messaging */}
            <SectionCard
              id="messaging"
              title="Messaging"
              hint="What users see in-app"
              status={sectionStatus.messaging.status}
              statusLabel={sectionStatus.messaging.label}
              openSections={openSections}
              toggleSection={toggleSection}
            >
              <div className={classes.formGrid2}>
                <CommonInput
                  label="Banner Title"
                  value={form.banner_title}
                  onChange={(e) => onChangeForm("banner_title", e.target.value)}
                />
                <CommonInput
                  label="Notification Title"
                  value={form.notification_title}
                  onChange={(e) => onChangeForm("notification_title", e.target.value)}
                />
              </div>
              <div className={classes.formGrid2} style={{ marginTop: "14px" }}>
                <CommonInput
                  label="Banner Body"
                  multiline
                  minRows={2}
                  value={form.banner_body}
                  onChange={(e) => onChangeForm("banner_body", e.target.value)}
                />
                <CommonInput
                  label="Notification Body"
                  multiline
                  minRows={2}
                  value={form.notification_body}
                  onChange={(e) => onChangeForm("notification_body", e.target.value)}
                />
              </div>
              <div className={classes.switchRow}>
                <FormControlLabel
                  control={
                    <CommonSwitch
                      checked={!!form.send_in_app}
                      onChange={(e) => onChangeForm("send_in_app", e.target.checked)}
                    />
                  }
                  classes={{ label: classes.switchLabel }}
                  label="Push"
                />
                <FormControlLabel
                  control={
                    <CommonSwitch
                      checked={!!form.send_sms}
                      onChange={(e) => onChangeForm("send_sms", e.target.checked)}
                    />
                  }
                  classes={{ label: classes.switchLabel }}
                  label="SMS"
                />
                <FormControlLabel
                  control={
                    <CommonSwitch
                      checked={!!form.send_email}
                      onChange={(e) => onChangeForm("send_email", e.target.checked)}
                    />
                  }
                  classes={{ label: classes.switchLabel }}
                  label="Email"
                />
              </div>
            </SectionCard>

            {/* Eligibility */}
            <SectionCard
              id="eligibility"
              title="Eligibility Rules"
              hint={`${(form.eligibility_rules || []).length} rule${(form.eligibility_rules || []).length === 1 ? "" : "s"}`}
              status={sectionStatus.eligibility.status}
              statusLabel={sectionStatus.eligibility.label}
              openSections={openSections}
              toggleSection={toggleSection}
            >
              {fieldErrors.eligibility_rules ? (
                <div className={classes.fieldError} style={{ marginBottom: "10px" }}>
                  {fieldErrors.eligibility_rules}
                </div>
              ) : null}
              {(form.eligibility_rules || []).map((rule, index) => (
                <div className={classes.ruleCard} key={`rule-${index}`}>
                  <div className={classes.ruleHeader}>
                    <span className={classes.ruleTitle}>Rule {index + 1}</span>
                    <Tooltip title={(form.eligibility_rules || []).length === 1 ? "Keep at least one rule" : "Remove rule"}>
                      <span>
                        <IconButton
                          size="small"
                          disabled={(form.eligibility_rules || []).length === 1}
                          onClick={() =>
                            onChangeForm(
                              "eligibility_rules",
                              form.eligibility_rules.filter((_, i) => i !== index),
                            )
                          }
                        >
                          <DeleteOutlineIcon fontSize="small" style={{ color: theme.colors.RejectRed }} />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </div>
                  <div className={classes.formGrid3}>
                    <FieldDropdown classes={classes}
                      label="Rule Type"
                      required
                      error={ruleErrors?.[index]?.rule_type}
                    >
                      <CommonDropDown
                        placeholder="Type"
                        options={configOptions.ruleTypes}
                        value={configOptions.ruleTypes.find((x) => x.value === rule.rule_type) || null}
                        isDisabled={!configReady}
                        onChange={(o) => {
                          const next = [...form.eligibility_rules];
                          next[index] = { ...next[index], rule_type: o?.value || "" };
                          onChangeForm("eligibility_rules", next);
                        }}
                      />
                    </FieldDropdown>
                    <FieldDropdown classes={classes}
                      label="Operator"
                      required
                      error={ruleErrors?.[index]?.operator}
                    >
                      <CommonDropDown
                        placeholder="Operator"
                        options={configOptions.ruleOperators}
                        value={configOptions.ruleOperators.find((x) => x.value === rule.operator) || null}
                        isDisabled={!configReady}
                        onChange={(o) => {
                          const next = [...form.eligibility_rules];
                          next[index] = { ...next[index], operator: o?.value || "" };
                          onChangeForm("eligibility_rules", next);
                        }}
                      />
                    </FieldDropdown>
                    <FieldDropdown classes={classes} label="Logic">
                      <CommonDropDown
                        placeholder="AND / OR"
                        options={configOptions.ruleLogics}
                        value={configOptions.ruleLogics.find((x) => x.value === rule.logic) || null}
                        isDisabled={!configReady}
                        onChange={(o) => {
                          const next = [...form.eligibility_rules];
                          next[index] = { ...next[index], logic: o?.value || "" };
                          onChangeForm("eligibility_rules", next);
                        }}
                      />
                    </FieldDropdown>
                  </div>
                  {(() => {
                    // One adaptive Value field per rule — mirrors the
                    // SegmentBuilder pattern. The data still rides under
                    // value_number / value_text / value_json on the wire;
                    // sanitizeEligibilityRules picks the right slot by kind.
                    if (!rule.rule_type) {
                      return (
                        <div className={classes.fieldHint}>
                          Pick a rule type to enter a value.
                        </div>
                      );
                    }
                    const kind = getRuleValueKind(rule.rule_type);
                    const setRuleField = (key, value) => {
                      const next = [...form.eligibility_rules];
                      next[index] = { ...next[index], [key]: value };
                      onChangeForm("eligibility_rules", next);
                    };
                    if (kind === "number") {
                      return (
                        <CommonInput
                          label="Value"
                          type="number"
                          placeholder="Numeric threshold"
                          value={rule.value_number}
                          onChange={(e) => setRuleField("value_number", e.target.value)}
                        />
                      );
                    }
                    if (kind === "text") {
                      return (
                        <CommonInput
                          label="Value"
                          placeholder="e.g. PREMIUM"
                          value={rule.value_text}
                          onChange={(e) => setRuleField("value_text", e.target.value)}
                        />
                      );
                    }
                    if (kind === "list") {
                      // EMPLOYER is the only list rule whose values are entity
                      // UUIDs — give it the multi-select picker so admins don't
                      // need to copy/paste company ids. The other list kinds
                      // (USER_TAG, USER_EMAILS) stay free-form text.
                      if (rule.rule_type === "EMPLOYER") {
                        const selectedIds = Array.isArray(rule.value_json)
                          ? rule.value_json
                          : (typeof rule.value_json === "string" && rule.value_json
                            ? rule.value_json.split(",").map((s) => s.trim()).filter(Boolean)
                            : []);
                        return (
                          <FieldDropdown classes={classes} label="Employers" hint="Pick one or more companies — campaign applies to their employees">
                            <EmployerSelect
                              value={selectedIds}
                              onChange={(ids) => setRuleField("value_json", ids)}
                              isDisabled={!configReady}
                              placeholder="Search and pick employers"
                            />
                          </FieldDropdown>
                        );
                      }
                      return (
                        <CommonInput
                          label="Values (comma-separated)"
                          placeholder="e.g. acme, globex, initech"
                          value={rule.value_json}
                          onChange={(e) => setRuleField("value_json", e.target.value)}
                        />
                      );
                    }
                    return (
                      <CommonInput
                        label="Value (JSON)"
                        placeholder='{"key":"value"} or [1, 2, 3]'
                        multiline
                        minRows={2}
                        value={rule.value_json}
                        error={!!ruleErrors?.[index]?.value_json}
                        helperText={ruleErrors?.[index]?.value_json || "Raw JSON value"}
                        onChange={(e) => setRuleField("value_json", e.target.value)}
                      />
                    );
                  })()}
                </div>
              ))}
              <div className={classes.addRuleRow}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<AddIcon />}
                  className={classes.addRuleBtn}
                  disabled={!configReady}
                  onClick={() =>
                    onChangeForm("eligibility_rules", [...(form.eligibility_rules || []), { ...emptyRule }])
                  }
                >
                  Add Rule
                </Button>
              </div>
            </SectionCard>

            {/* Footer */}
            <div className={classes.formFooter}>
              {!requiredFilled ? (
                <span className={classes.fieldHint} style={{ alignSelf: "center", marginRight: "auto" }}>
                  Fill the required fields to enable {mode === "create" ? "Create" : "Save"}.
                </span>
              ) : null}
              <CommonButton
                type="button"
                text="Cancel"
                stylevariant="secondary2"
                onClick={cancelForm}
                style={{ padding: "8px 24px" }}
              />
              <CommonButton
                type="button"
                text={ui?.saveLoading ? "Saving..." : (mode === "create" ? "Create Campaign" : "Save Changes")}
                stylevariant="primary"
                disabled={ui?.saveLoading || !configReady || !requiredFilled}
                onClick={submitForm}
                style={{ padding: "8px 28px" }}
              />
            </div>
          </div>

          {/* Live preview */}
          <aside className={classes.previewWrap}>
            <div className={classes.previewCard}>
              <div className={classes.previewLabel}>Reward summary</div>
              <div className={classes.rewardCellStack}>
                <Chip className={classes.previewRewardChip} label={rewardSummary(form)} />
                {(() => {
                  const ch = channelSummary(form);
                  return (
                    <Tooltip title={ch.warning || ch.text}>
                      <Chip
                        className={`${classes.channelChip} ${ch.isEmpty ? classes.channelChipWarning : ""}`}
                        label={ch.text}
                      />
                    </Tooltip>
                  );
                })()}
              </div>
              {(() => {
                const w = rewardWarning(form);
                if (!w) return null;
                return (
                  <div style={{ marginTop: "10px" }}>
                    <Chip
                      className={`${classes.channelChip} ${classes.channelChipWarning}`}
                      label={w}
                    />
                  </div>
                );
              })()}
              {form.max_budget ? (
                <div style={{ marginTop: "10px" }}>
                  <span className={classes.fieldHint}>
                    Budget cap: {formatMoney(form.max_budget, form.reward_currency)}
                  </span>
                </div>
              ) : null}
              {form.max_redemptions_per_user ? (
                <div style={{ marginTop: "4px" }}>
                  <span className={classes.fieldHint}>
                    {form.max_redemptions_per_user} per user
                    {form.cap_window ? ` per ${form.cap_window.toLowerCase()}` : ""}
                  </span>
                </div>
              ) : null}
            </div>

            <div className={classes.previewCard}>
              <div className={classes.previewLabel}>How users see it</div>
              <div className={classes.previewMobileFrame}>
                {form.send_in_app ? (
                  form.notification_title || form.notification_body ? (
                    <div className={classes.previewNotification}>
                      {form.notification_title ? (
                        <div className={classes.previewNotificationTitle}>
                          {form.notification_title}
                        </div>
                      ) : null}
                      {form.notification_body ? (
                        <div className={classes.previewNotificationBody}>
                          {form.notification_body}
                        </div>
                      ) : null}
                    </div>
                  ) : (
                    <div className={classes.previewNotification}>
                      <div className={classes.previewPlaceholder}>Push notification preview appears here</div>
                    </div>
                  )
                ) : null}

                {/* SMS preview — green message bubble using notification_body
                    so admins see what the SMS will actually read like on a
                    phone. notification_title doubles as the sender label. */}
                {form.send_sms ? (
                  <div className={classes.previewSmsWrap}>
                    <span className={classes.previewSmsSender}>
                      {form.notification_title || "Sanaam"}
                    </span>
                    <div className={classes.previewSmsBubble}>
                      {form.notification_body || "SMS body appears here"}
                    </div>
                  </div>
                ) : null}

                {form.banner_title || form.banner_body ? (
                  <div className={classes.previewBanner}>
                    {form.banner_title ? (
                      <div className={classes.previewBannerTitle}>{form.banner_title}</div>
                    ) : null}
                    {form.banner_body ? (
                      <div className={classes.previewBannerBody}>{form.banner_body}</div>
                    ) : null}
                  </div>
                ) : (
                  <div className={classes.previewBanner}>
                    <div className={classes.previewBannerTitle} style={{ opacity: 0.7 }}>Banner title</div>
                    <div className={classes.previewBannerBody}>Banner body shows up here</div>
                  </div>
                )}
              </div>
            </div>
          </aside>
        </div>
      )}

      {mode === "detail" && (() => {
        // The detail render runs only once the cached `detail` matches the id
        // the user clicked. Otherwise (first open, or switching from another
        // campaign) show a centered loader so the user isn't staring at blank
        // canvas while the fetch is in flight.
        const isFreshFetch = ui?.detailLoading || !detail;
        const isStale = selectedCampaignId && detail?.id && detail.id !== selectedCampaignId;
        if (isFreshFetch || isStale) {
          return (
            <div className={classes.detailLoading}>
              <CircularProgress size={36} style={{ color: theme.colors.DodgerBlue }} />
              <div className={classes.detailLoadingLabel}>Loading campaign…</div>
              <div className={classes.detailLoadingHint}>Fetching details, stats and history.</div>
              <CommonButton
                type="button"
                text="Back to List"
                stylevariant="secondary2"
                onClick={() => setMode("list")}
                style={{ padding: "6px 18px", marginTop: "6px" }}
              />
            </div>
          );
        }

        const reward = rewardSummary(detail);
        const warning = rewardWarning(detail);
        const channel = channelSummary(detail);
        const eligibility = summarizeEligibility(detail.eligibility_rules);
        const isTerminal = detail.state === "ENDED" || detail.state === "CANCELLED";
        const hasCap = detail.max_redemptions_per_user != null;
        const stateColor = detail.state === "ACTIVE"
          ? theme.colors.ApproveGreen
          : detail.state === "CANCELLED" || detail.state === "ENDED"
            ? "#64748B"
            : theme.colors.DodgerBlue;
        const fmtTs = (v) => (v ? moment(v).format("MMM Do YY, h:mm a") : "—");

        return (
          <div className={classes.detailLayout}>
            <div className={classes.detailColumn}>
              {/* Header card: title + state + action buttons (hidden when flag is false). */}
              <div className={classes.detailCard}>
                <div className={classes.detailHeaderRow}>
                  <div>
                    <h3 className={classes.detailTitle}>
                      {detail.name}
                      {detail.code ? <span className={classes.detailCode}>· {detail.code}</span> : null}
                    </h3>
                    <div className={classes.detailHeaderChips}>
                      <Chip
                        size="small"
                        label={detail.state || "—"}
                        style={{ backgroundColor: stateColor, color: theme.colors.White, fontFamily: theme.fontFamily.MARKPROMEDIUM }}
                      />
                      <Chip size="small" label={detail.campaign_type || "—"} />
                      {!detail.is_enabled ? (
                        <Chip size="small" label="Disabled" style={{ backgroundColor: theme.colors.Cinderella, color: theme.colors.RejectRed }} />
                      ) : null}
                    </div>
                  </div>
                  <div className={classes.actionsRow}>
                    {detail?.available_actions?.can_edit && (
                      <CommonButton type="button" text="Edit" stylevariant="secondary2" onClick={launchEdit} style={{ padding: "6px 18px" }} />
                    )}
                    {isSuper && detail?.available_actions?.can_submit_review && (
                      <CommonButton type="button" text="Submit Review" stylevariant="secondary2" onClick={() => setActionModal({ open: true, type: "submit-review", comment: "" })} style={{ padding: "6px 18px" }} />
                    )}
                    {isSuper && detail?.available_actions?.can_approve && (
                      <CommonButton type="button" text="Approve" stylevariant="primary" onClick={() => setActionModal({ open: true, type: "approve", comment: "" })} style={{ padding: "6px 18px" }} />
                    )}
                    {isSuper && detail?.available_actions?.can_activate && (
                      <CommonButton type="button" text="Activate" stylevariant="primary" onClick={() => setActionModal({ open: true, type: "activate", comment: "" })} style={{ padding: "6px 18px" }} />
                    )}
                    {detail?.available_actions?.can_deactivate && (
                      <CommonButton type="button" text="Deactivate" stylevariant="secondary2" onClick={() => setActionModal({ open: true, type: "deactivate", comment: "" })} style={{ padding: "6px 18px" }} />
                    )}
                    {isSuper && detail?.available_actions?.can_end && (
                      <CommonButton type="button" text="End" stylevariant="danger" onClick={() => setActionModal({ open: true, type: "end", comment: "" })} style={{ padding: "6px 18px" }} />
                    )}
                  </div>
                </div>
                {detail.description ? (
                  <div className={classes.detailMetaValue} style={{ fontFamily: theme.fontFamily.MARKPRO, fontSize: "13px", color: theme.colors.Violet }}>
                    {detail.description}
                  </div>
                ) : null}
              </div>

              {/* Schedule + scope. is_recurring/recurrence_type are noise for
                  ENDED/CANCELLED campaigns — hide them per spec. */}
              <div className={classes.detailCard}>
                <div className={classes.detailLabel}>Schedule</div>
                <div className={classes.detailMetaGrid}>
                  <div>
                    <div className={classes.detailMetaLabel}>Start</div>
                    <div className={classes.detailMetaValue}>{fmtTs(detail.start_at)}</div>
                  </div>
                  <div>
                    <div className={classes.detailMetaLabel}>End</div>
                    <div className={classes.detailMetaValue}>{fmtTs(detail.end_at)}</div>
                  </div>
                  <div>
                    <div className={classes.detailMetaLabel}>Country</div>
                    <div className={classes.detailMetaValue}>{detail.country || "Global"}</div>
                  </div>
                  <div>
                    <div className={classes.detailMetaLabel}>Priority</div>
                    <div className={classes.detailMetaValue}>{detail.priority ?? "—"}</div>
                  </div>
                  {!isTerminal ? (
                    <div>
                      <div className={classes.detailMetaLabel}>Recurring</div>
                      <div className={classes.detailMetaValue}>
                        {detail.is_recurring
                          ? (detail.recurrence_type ? detail.recurrence_type : "Yes")
                          : "No"}
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>

              {/* Reward, channel, warnings */}
              <div className={classes.detailCard}>
                <div className={classes.detailLabel}>Reward & channel</div>
                <div className={classes.detailHeaderChips}>
                  <Chip className={classes.previewRewardChip} label={reward} />
                  <Tooltip title={channel.warning || channel.text}>
                    <Chip
                      className={`${classes.channelChip} ${channel.isEmpty ? classes.channelChipWarning : ""}`}
                      label={channel.text}
                    />
                  </Tooltip>
                </div>
                {warning ? (
                  <div style={{ marginTop: "10px" }}>
                    <Chip className={classes.warningChip} label={warning} />
                  </div>
                ) : null}
              </div>

              {/* Caps panel — max_redemptions_per_user and cap_window are
                  meaningless apart; render them together with explicit "no cap"
                  labels per the spec. */}
              <div className={classes.detailCard}>
                <div className={classes.detailLabel}>Caps</div>
                <div className={classes.detailMetaGrid}>
                  <div>
                    <div className={classes.detailMetaLabel}>Per user</div>
                    <div className={classes.detailMetaValue}>
                      {hasCap
                        ? `${detail.max_redemptions_per_user} per ${detail.cap_window ? detail.cap_window.toLowerCase() : "—"}`
                        : "No cap"}
                    </div>
                  </div>
                  <div>
                    <div className={classes.detailMetaLabel}>Global redemptions</div>
                    <div className={classes.detailMetaValue}>
                      {detail.max_total_redemptions != null ? detail.max_total_redemptions : "No cap"}
                    </div>
                  </div>
                  <div>
                    <div className={classes.detailMetaLabel}>Budget</div>
                    <div className={classes.detailMetaValue}>
                      {detail.max_budget != null ? formatMoney(detail.max_budget, detail.reward_currency) : "No cap"}
                    </div>
                  </div>
                </div>
              </div>

              {/* Eligibility */}
              <div className={classes.detailCard}>
                <div className={classes.detailLabel}>Eligibility</div>
                {eligibility.empty ? (
                  <div className={classes.emptyHint}>{eligibility.text}</div>
                ) : (
                  <div>
                    {(detail.eligibility_rules || []).map((rule, idx) => (
                      <div className={classes.ruleItem} key={rule.id || idx}>
                        <div>{humanizeRule(rule)}</div>
                        <div className={classes.ruleMeta}>
                          Logic: {rule.logic || "AND"} · Group: {rule.group || "default"}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Messaging copy */}
              {(detail.banner_title || detail.banner_body || detail.notification_title || detail.notification_body) ? (
                <div className={classes.detailCard}>
                  <div className={classes.detailLabel}>Messaging</div>
                  <div className={classes.detailMetaGrid}>
                    <div>
                      <div className={classes.detailMetaLabel}>Banner</div>
                      <div className={classes.detailMetaValue}>{detail.banner_title || "—"}</div>
                      <div style={{ fontSize: "12px", color: "#64748B", marginTop: "2px" }}>
                        {detail.banner_body || "—"}
                      </div>
                    </div>
                    <div>
                      <div className={classes.detailMetaLabel}>Notification</div>
                      <div className={classes.detailMetaValue}>{detail.notification_title || "—"}</div>
                      <div style={{ fontSize: "12px", color: "#64748B", marginTop: "2px" }}>
                        {detail.notification_body || "—"}
                      </div>
                    </div>
                  </div>
                </div>
              ) : null}

              {/* Audit timeline: workflow milestones from the detail object,
                  followed by the history feed from /audit. */}
              <div className={classes.detailCard}>
                <div className={classes.detailLabel}>Audit timeline</div>
                <div>
                  <div className={classes.auditRow}>
                    <span className={classes.auditAction}>Created</span>
                    <span className={classes.auditActor}>{detail.created_by || "—"}</span>
                    <span className={classes.auditTime}>{fmtTs(detail.created_at)}</span>
                  </div>
                  {detail.reviewed_at ? (
                    <div className={classes.auditRow}>
                      <span className={classes.auditAction}>Reviewed</span>
                      <span className={classes.auditActor}>{detail.reviewed_by || "—"}</span>
                      <span className={classes.auditTime}>{fmtTs(detail.reviewed_at)}</span>
                    </div>
                  ) : null}
                  {detail.approved_at ? (
                    <div className={classes.auditRow}>
                      <span className={classes.auditAction}>Approved</span>
                      <span className={classes.auditActor}>{detail.approved_by || "—"}</span>
                      <span className={classes.auditTime}>{fmtTs(detail.approved_at)}</span>
                    </div>
                  ) : null}
                  {(history?.data || []).map((entry, idx) => (
                    <div className={classes.auditRow} key={entry.id || idx}>
                      <span className={classes.auditAction}>{entry.action || "—"}</span>
                      <span className={classes.auditActor}>{entry.actor || "—"}</span>
                      <span className={classes.auditTime}>{fmtTs(entry.created_at)}</span>
                    </div>
                  ))}
                  {!history?.data?.length && !detail.reviewed_at && !detail.approved_at ? (
                    <div className={classes.emptyHint} style={{ marginTop: "10px" }}>
                      No further audit entries yet.
                    </div>
                  ) : null}
                </div>
                {(history?.count || 0) > (historyPagination.page_size || 10) ? (
                  <div className={classes.paginationWrap}>
                    <Pagination
                      page={historyPagination.page}
                      count={Math.max(1, Math.ceil((history?.count || 0) / (historyPagination.page_size || 10)))}
                      onChange={(_, page) => dispatch(setCampaignHistoryPaginationAction({ page }))}
                    />
                  </div>
                ) : null}
              </div>
            </div>

            {/* Side rail: live stats from /stats */}
            <aside className={classes.previewWrap}>
              <div className={classes.detailCard}>
                <div className={classes.detailLabel}>Live stats</div>
                <div className={classes.detailMetaGrid} style={{ gridTemplateColumns: "1fr 1fr" }}>
                  <div>
                    <div className={classes.detailMetaLabel}>Eligible</div>
                    <div className={classes.detailMetaValue} style={{ fontSize: "20px" }}>
                      {stats?.eligible_count ?? "—"}
                    </div>
                    {stats && stats.is_exact === false ? (
                      <div style={{ fontSize: "11px", color: "#64748B" }}>approximate</div>
                    ) : null}
                  </div>
                  <div>
                    <div className={classes.detailMetaLabel}>Redeemed</div>
                    <div className={classes.detailMetaValue} style={{ fontSize: "20px" }}>
                      {stats?.redeemed_count ?? "—"}
                    </div>
                  </div>
                  <div style={{ gridColumn: "1 / -1" }}>
                    <div className={classes.detailMetaLabel}>Total cost</div>
                    <div className={classes.detailMetaValue}>
                      {stats?.total_cost != null ? formatMoney(stats.total_cost, detail.reward_currency) : "—"}
                    </div>
                  </div>
                </div>
              </div>
            </aside>
          </div>
        );
      })()}

      <Dialog open={actionModal.open} onClose={() => setActionModal({ open: false, type: "", comment: "" })}>
        <DialogTitle
          disableTypography
          style={{
            color: theme.colors.DodgerBlue,
            fontFamily: theme.fontFamily.MARKPROBOLD,
            fontSize: "16px",
            padding: "16px 24px",
          }}
        >
          Confirm {actionModal.type}
        </DialogTitle>
        <DialogContent>
          <CommonInput
            label="Comment (optional)"
            value={actionModal.comment}
            onChange={(e) => setActionModal((prev) => ({ ...prev, comment: e.target.value }))}
          />
        </DialogContent>
        <DialogActions style={{ padding: "12px 24px" }}>
          <CommonButton
            type="button"
            text="Cancel"
            stylevariant="secondary2"
            onClick={() => setActionModal({ open: false, type: "", comment: "" })}
            style={{ padding: "6px 18px" }}
          />
          <CommonButton
            type="button"
            text={ui?.actionLoading ? "Processing..." : "Confirm"}
            stylevariant="primary"
            disabled={ui?.actionLoading}
            onClick={submitAction}
            style={{ padding: "6px 18px" }}
          />
        </DialogActions>
      </Dialog>
    </div>
  );
}


const SectionCard = ({ id, title, hint, status, statusLabel, openSections, toggleSection, children }) => {
  const classes = useStyles();
  const isOpen = openSections && !!openSections[id];
  const statusClass =
    status === "complete"
      ? classes.sectionStatusComplete
      : status === "missing"
        ? classes.sectionStatusMissing
        : classes.sectionStatusPending;
  return (
    <div className={classes.formCard}>
      <div className={classes.sectionToggle} onClick={() => toggleSection(id)}>
        <div className={classes.sectionTitleWrap}>
          <h3 className={classes.formSectionTitle}>{title}</h3>
          {hint ? <span className={classes.formSectionHint}>{hint}</span> : null}
        </div>
        {statusLabel ? (
          <span className={statusClass}>
            {status === "complete" ? (
              <CheckCircleOutlineIcon style={{ fontSize: "13px", marginRight: "3px" }} />
            ) : null}
            {statusLabel}
          </span>
        ) : null}
        {isOpen ? (
          <ExpandMore className={`${classes.sectionCaret} sectionCaret`} />
        ) : (
          <ChevronRight className={`${classes.sectionCaret} sectionCaret`} />
        )}
      </div>
      <Collapse in={isOpen} timeout="auto" unmountOnExit>
        <div className={classes.sectionBody}>{children}</div>
      </Collapse>
    </div>
  );
};